/**
 * RequestSubstitutionDialog — Phase 5 create UX.
 *
 * Single-purpose modal that creates a SubstitutionRequest via the canonical
 * `createSubstitutionRequest` writer. No automation, no notifications,
 * no scheduling effects. Append-only.
 */
import { useState } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useProcurementWriters } from "@/data/procurement/procurementWriters";
import type {
  SubstitutionReason,
  SubstitutionCustomerImpact,
} from "@/data/VendorPOContext";
import { toast } from "@/hooks/use-toast";

const REASONS: { value: SubstitutionReason; label: string }[] = [
  { value: "vendor_unavailable", label: "Vendor unavailable" },
  { value: "vendor_delay", label: "Vendor delay" },
  { value: "cost_variance", label: "Cost variance" },
  { value: "spec_change", label: "Spec change" },
  { value: "damage_replacement", label: "Damage replacement" },
  { value: "field_issue", label: "Field issue" },
  { value: "other", label: "Other" },
];

const IMPACTS: { value: SubstitutionCustomerImpact; label: string }[] = [
  { value: "none", label: "None" },
  { value: "visual_change", label: "Visual change" },
  { value: "functional_change", label: "Functional change" },
  { value: "spec_change", label: "Spec change" },
  { value: "unknown", label: "Unknown" },
];

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  jobId: string;
  actor: string;
  defaults?: {
    originalDescription?: string;
    originalMaterialReference?: string;
    reason?: SubstitutionReason;
  };
}

export function RequestSubstitutionDialog({ open, onOpenChange, jobId, actor, defaults }: Props) {
  const { createSubstitutionRequest } = useProcurementWriters();

  const [originalDescription, setOriginalDescription] = useState(defaults?.originalDescription ?? "");
  const [originalMaterialReference, setOriginalMaterialReference] = useState(
    defaults?.originalMaterialReference ?? "",
  );
  const [proposedDescription, setProposedDescription] = useState("");
  const [proposedMaterialReference, setProposedMaterialReference] = useState("");
  const [reason, setReason] = useState<SubstitutionReason>(defaults?.reason ?? "vendor_unavailable");
  const [customerImpact, setCustomerImpact] = useState<SubstitutionCustomerImpact>("unknown");
  const [customerApprovalRequired, setCustomerApprovalRequired] = useState(false);

  function reset() {
    setOriginalDescription(defaults?.originalDescription ?? "");
    setOriginalMaterialReference(defaults?.originalMaterialReference ?? "");
    setProposedDescription("");
    setProposedMaterialReference("");
    setReason(defaults?.reason ?? "vendor_unavailable");
    setCustomerImpact("unknown");
    setCustomerApprovalRequired(false);
  }

  function handleSubmit() {
    try {
      createSubstitutionRequest(
        {
          jobId,
          originalDescription,
          ...(originalMaterialReference ? { originalMaterialReference } : {}),
          proposedDescription,
          ...(proposedMaterialReference ? { proposedMaterialReference } : {}),
          reason,
          customerImpact,
          customerApprovalRequired,
        },
        actor,
      );
      toast({ title: "Substitution requested", description: "Request logged for review." });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Could not submit",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request Substitution</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Original material *</Label>
            <Input
              data-testid="sub-original-description"
              className="mt-1 h-9 text-sm"
              placeholder="e.g. Delta R10000 valve trim"
              value={originalDescription}
              onChange={(e) => setOriginalDescription(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Original SKU / reference (optional)</Label>
            <Input
              className="mt-1 h-9 text-sm"
              value={originalMaterialReference}
              onChange={(e) => setOriginalMaterialReference(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Proposed substitute *</Label>
            <Input
              data-testid="sub-proposed-description"
              className="mt-1 h-9 text-sm"
              placeholder="e.g. Moen 2520 valve trim"
              value={proposedDescription}
              onChange={(e) => setProposedDescription(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Proposed SKU / reference (optional)</Label>
            <Input
              className="mt-1 h-9 text-sm"
              value={proposedMaterialReference}
              onChange={(e) => setProposedMaterialReference(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Reason *</Label>
            <select
              data-testid="sub-reason"
              className="w-full mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value as SubstitutionReason)}
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Customer impact</Label>
            <select
              className="w-full mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={customerImpact}
              onChange={(e) => setCustomerImpact(e.target.value as SubstitutionCustomerImpact)}
            >
              {IMPACTS.map((i) => (
                <option key={i.value} value={i.value}>{i.label}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={customerApprovalRequired}
              onChange={(e) => setCustomerApprovalRequired(e.target.checked)}
            />
            <span>Customer approval required</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <PermissionGate permission="ordering_create">
            <Button
              size="sm"
              data-testid="sub-submit"
              disabled={!originalDescription.trim() || !proposedDescription.trim()}
              onClick={handleSubmit}
            >
              Submit request
            </Button>
          </PermissionGate>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
